import { FieldArray } from 'formik'
import { isEmpty } from 'ramda'
import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { CollapsibleBox } from '@/components/collapsible'
import { FieldInput, FieldDropdown, FieldTextarea } from '@/components/form'
import Icon from '@/components/icon'
import useMediaQuery from '@/hooks/use-media-query'
import { colors, fontWeights, fontSizes, breakpoints } from '@/styles'
import { FormGrid, FormRow, SectionTitle } from '../../elements'

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const EditButton = styled.button.attrs({ type: 'button' })`
  height: 30px;
  width: auto;
  padding: 0 15px;
  border: 1px solid ${colors.mediumGrey};
  border-radius: 2px;
  background-color: transparent;
  cursor: pointer;
  color: ${colors.secondary};
  font-size: 12px;
  font-weight: ${fontWeights.medium};
  text-align: center;

  :hover {
    opacity: 0.7;
  }
`

const MobileEditButton = styled.button`
  height: 66px;
  width: 100%;
  border-radius: 6px;
  border: 1px solid ${colors.grey};
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${colors.regular};
  font-size: ${fontSizes.regular};
  font-weight: ${fontWeights.medium};
  line-height: 22px;
  padding: 20px;
  background-color: transparent;
  cursor: pointer;
  margin-top: 30px;
`

const DeleteButton = styled.p`
  color: ${colors.link};
  font-size: 12px;
  margin: 0;
  font-weight: ${fontWeights.medium};
  cursor: pointer;
`

const FeeHeader = styled.div`
  background-color: ${colors.grey96};
  border-bottom: 1px solid ${colors.grey80};
  padding: 16px;
  font-weight: ${fontWeights.bold};
  color: ${colors.black};
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top-left-radius: inherit;
  border-top-right-radius: inherit;
`

const EmptyMessage = styled.p`
  font-size: 16px;
  line-height: 22px;
  margin: 0;
`

const ExtraFeeList = ({ data, contractId }) => {
  const { t } = useTranslation()
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)

  const newExtraFee = {
    contract_id: contractId || '',
    fee: '',
    fee_frequency_txt_id: '',
    fee_txt_id: 'misc',
    name: ''
  }

  const renderFee = (fee, index, arrayHelpers) => {
    return (
      <CollapsibleBox
        key={index}
        title={`${t('l.contract.edit.extra_fee_payment.label', 'Extra Fee / Payment')} ${
          index + 1
        }`}
        maxLength={22}
        top={30}
        header={FeeHeader}
        headerToggle={
          <DeleteButton onClick={() => arrayHelpers.remove(index)}>
            {t('common.delete', 'Delete')}
          </DeleteButton>
        }
      >
        <FormGrid>
          <FormRow>
            <FieldTextarea
              name={`additional_fees.${index}.name`}
              maxLength={250}
              height={150}
              placeholder={t('l.contract.edit.write_info.placeholder', 'Write additional info')}
              validateOnChange
            />
          </FormRow>
          <div>
            <FieldInput
              name={`additional_fees.${index}.fee`}
              placeholder={t('l.contract.edit.amount.label', 'Amount')}
              required
              prefix="$"
              transform={val => (val ?? '').replace(/[^0-9.]/g, '')}
              validateOnChange
            />
          </div>
          <div>
            <FieldDropdown
              name={`additional_fees.${index}.fee_frequency_txt_id`}
              placeholder={t('l.contract.edit.payment_frequency.label', 'Payment frequency')}
              required
              options={[
                {
                  value: 'weekly',
                  label: t('common.frequency_weekly', 'Weekly')
                },
                {
                  value: 'monthly',
                  label: t('common.frequency_monthly', 'Monthly')
                },
                {
                  value: 'one_time',
                  label: t('common.frequency_one_time', 'One-time')
                }
              ]}
              validateOnChange
            />
          </div>
        </FormGrid>
      </CollapsibleBox>
    )
  }

  return (
    <FieldArray
      name="additional_fees"
      render={arrayHelpers => (
        <>
          <TitleRow>
            <SectionTitle>
              {t('l.contract.edit.extra_fees_payment.subtitle', 'Extra Fees / Payments')}
            </SectionTitle>
            {!displayMobile && (
              <EditButton onClick={() => arrayHelpers.push(newExtraFee)}>
                + {t('l.contract.edit.add_extra_fee.button', 'Add extra fee / payment')}
              </EditButton>
            )}
          </TitleRow>
          {isEmpty(data) && !displayMobile && (
            <EmptyMessage>
              {t('l.contract.edit.empty_extra_fees.text', 'No extra fees / payments added yet')}
            </EmptyMessage>
          )}
          {!isEmpty(data) && data.map((fee, index) => renderFee(fee, index, arrayHelpers))}
          {displayMobile && (
            <MobileEditButton type="button" onClick={() => arrayHelpers.push(newExtraFee)}>
              {t('l.contract.edit.add_extra_fee.button', 'Add extra fee / payment')}
              <Icon id="plus" height={18} width={18} />
            </MobileEditButton>
          )}
        </>
      )}
    />
  )
}

export default memo(ExtraFeeList)
